import Big from "big.js";
import {
  Entity,
  LedgerAgreementFeeEntry,
  LedgerEntry,
  RecoveryAgainstPayment,
} from "../Analytics.ts";
import { CostType } from "../CaseConfiguration.ts";
import { ZERO } from "../bigUtil.ts";
import {
  LedgerItems,
  OriginId,
  OriginItem,
  PaymentId,
  PaymentItem,
  agreementOrigin,
  awardedCostsOrder,
  entityOf,
  isOriginOfKind,
} from "./items.ts";

/**
 * Projection: the provenance item DAG → the legacy wire (`LedgerEntry[]`).
 *
 * Every rule here is a pure function of edge shape — mechanism, cited origin
 * kinds, derivation — never of stored entry kinds (there are none) and never
 * of item ids. The projection owns the WIRE ordering: items are emitted in
 * dependency order (cited before citer), entries leave in the legacy
 * emission order (costs → law-firm fees → barrister fees → re-charges →
 * settlements → quantums → adverse costs → shortfall → enforcement), which
 * the totals' key/array ordering depends on.
 *
 * Recovery items are not entries: each reimburses the payment it cites, so
 * it folds into that host's `recovered` sub-element. The present-but-zero
 * sentinel (dialog "0" vs "–") derives from the costs-order origin's rules —
 * no zero-amount items exist.
 */
/**
 * The flat fold over the item DAG: payer subtracts, payee adds, opponents
 * and externals contribute nothing. The payer also pays the payment's VAT,
 * which the payee does not receive. By construction this equals the
 * projected wire's per-entity netResult (the validator asserts it) — the
 * provider gross-up arms of `signedAmountFor` are explicit items here.
 */
export function signedAmountForItem(item: PaymentItem, entity: Entity): number {
  return (
    (entityOf(item.payer) === entity ? -(item.amount + (item.vat ?? 0)) : 0) +
    (entityOf(item.payee) === entity ? item.amount : 0)
  );
}

export function projectEntries({
  context,
  origins,
  payments,
}: LedgerItems): LedgerEntry[] {
  // The function runs in three phases:
  //   1. pre-passes over the DAG (resolve citations, fold recovery items
  //      onto their hosts, derive the recovery-applicability regime);
  //   2. ONE classification pass over the payments — each item maps to at
  //      most one entry, decided purely by its edge shape;
  //   3. concatenation of the per-kind buckets in the legacy wire order.

  const originById = new Map<OriginId, OriginItem>();
  for (const origin of origins) originById.set(origin.id, origin);

  // Citation lookups resolve LENIENTLY (a dangling id contributes nothing) —
  // referential integrity is the validator's job, the projection must never
  // throw on the data it is given.
  const citedOrigins = ({ causedBy }: PaymentItem): OriginItem[] =>
    causedBy.origins.flatMap((id) => {
      const origin = originById.get(id);
      return origin === undefined ? [] : [origin];
    });
  const citedOfKind = <K extends OriginItem["kind"]>(
    item: PaymentItem,
    kind: K,
  ): Extract<OriginItem, { kind: K }>[] =>
    citedOrigins(item).filter(isOriginOfKind(kind));

  // Items carry party IDS; the wire renders display NAMES (fromPayers,
  // toRecipients). The mapping is context the emission captured.
  const partyName = (partyId: string): string =>
    context.partyNames[partyId] ?? partyId;

  // --- recovery applicability regime (the costs order's rules) ---
  // Drives the dialog's tri-state per paid item: an attached recovery shows
  // its amount, a cost TYPE with a rule but nothing recovered this outcome
  // shows a present-but-zero "0", a type without a rule shows "–". The rules
  // live on the awarded costs-order origin, so no zero-amount payment items
  // are needed to encode applicability.
  const recoverableTypes = new Set<CostType>(
    (awardedCostsOrder(origins)?.rules ?? []).map(({ costType }) => costType),
  );
  const sentinelIf = (
    applicable: boolean,
  ): RecoveryAgainstPayment | undefined =>
    applicable ? { amount: 0, fromPayers: [] } : undefined;

  // A recovery is the one adjustment shape citing BOTH the awarded costs
  // order and the work item it reimburses. Recoveries never become entries
  // of their own — they ride the payment the party paid that work in.
  const isRecovery = (item: PaymentItem): boolean =>
    item.mechanism === "adjustment" &&
    citedOfKind(item, "work").length > 0 &&
    citedOfKind(item, "costsOrder").some((o) => o.direction === "awarded");

  // --- pre-pass: fold recoveries onto the payments they reimbur1se ---
  // Two domain shapes, discriminated by what the recovery cites:
  //
  //   - A normal recovery reimburses exactly ONE payment — the bill, the
  //     party-paid CFA base fee, or the re-charged bill the party paid that
  //     work in. It folds onto that payment's entry. Several recoveries can
  //     share one payment (every covered work item reimbursed on the same
  //     cfaBase line): amounts add up, payer names union in item order.
  //
  //   - An enforcement recovery reimburses SEVERAL payments collectively:
  //     the plan splits enforcement spend per enforced opponent while
  //     recoveries are computed per work item, so the two splits don't line
  //     up and the recovery relates to all of its cost type's plan
  //     payments. It folds per COST TYPE; the entry loop attaches each
  //     type's total to the first enforcement entry of that type (the same
  //     first-per-type rule as the applicability sentinel).
  //
  // The validator pins both shapes (exactly one reimbursed payment vs only
  // plan payments of the work's own cost type).
  const paymentById = new Map<PaymentId, PaymentItem>();
  for (const payment of payments) paymentById.set(payment.id, payment);
  const reimbursesPlanPayments = (item: PaymentItem): boolean =>
    (item.causedBy.payments ?? []).some(
      (id) =>
        paymentById.get(id)?.causedBy.derivation?.rule ===
        "enforcementApportionment",
    );

  // the recovery of the work and the recovery of the VAT on it fold apart
  type Recovered = { amount: Big; vat: Big; payers: string[] };
  const foldRecovery = (
    acc: Recovered | undefined,
    item: PaymentItem,
  ): Recovered => {
    const next = acc ?? { amount: ZERO, vat: ZERO, payers: [] };
    const derivation = item.causedBy.derivation;
    if (derivation?.rule === "recovery" && derivation.ofVat)
      next.vat = next.vat.add(item.amount);
    else next.amount = next.amount.add(item.amount);
    const payerIds =
      derivation?.rule === "recovery"
        ? derivation.payerShares.map(({ partyId }) => partyId)
        : [];
    for (const payerId of payerIds) {
      const name = partyName(payerId);
      if (!next.payers.includes(name)) next.payers.push(name);
    }
    return next;
  };

  const recoveredByPayment = new Map<PaymentId, Recovered>();
  const recoveredByEnforcementType = new Map<CostType, Recovered>();
  for (const item of payments) {
    if (!isRecovery(item)) continue;
    if (reimbursesPlanPayments(item)) {
      // a recovery reimburses exactly one work item — its cost type keys
      // the per-type fold
      const [work] = citedOfKind(item, "work");
      if (work === undefined) continue;
      recoveredByEnforcementType.set(
        work.costType,
        foldRecovery(recoveredByEnforcementType.get(work.costType), item),
      );
      continue;
    }
    const [reimbursedPayment] = item.causedBy.payments ?? [];
    if (reimbursedPayment === undefined) continue;
    recoveredByPayment.set(
      reimbursedPayment,
      foldRecovery(recoveredByPayment.get(reimbursedPayment), item),
    );
  }
  const toRecovered = (
    acc: Recovered | undefined,
  ): RecoveryAgainstPayment | undefined =>
    acc === undefined
      ? undefined
      : {
          amount: acc.amount.toNumber(),
          ...(acc.vat.gt(0) ? { vat: acc.vat.toNumber() } : {}),
          fromPayers: [...acc.payers],
        };
  const recoveredFor = (
    item: PaymentItem,
  ): RecoveryAgainstPayment | undefined =>
    toRecovered(recoveredByPayment.get(item.id));

  // Barrister-covered work types — the firm's base line is recoverable for
  // a barristerFee rule when its CFA covers barrister disbursements, even in
  // outcomes where no fees actually passed on (legacy `baseRecoverable`).
  const barristerAgr = agreementOrigin(origins, "barristerCfa");
  const barristerCoveredTypes = new Set<CostType>();
  if (barristerAgr !== undefined) {
    for (const item of payments) {
      if (item.mechanism !== "absorption") continue;
      if (!item.causedBy.origins.includes(barristerAgr.id)) continue;
      for (const work of citedOfKind(item, "work")) {
        barristerCoveredTypes.add(work.costType);
      }
    }
  }

  // --- entry buckets, concatenated in the legacy emission order ---
  // Items arrive in DEPENDENCY order (a cited payment precedes its citer),
  // which is not the wire order — e.g. barrister fees precede the law-firm
  // base that cites them, while the wire lists law-firm fees first. Bucket
  // per entry kind during the single pass, concatenate at the end. The order
  // is load-bearing: `aggregateTotals` builds its key/array ordering from
  // first occurrence.
  const costs: LedgerEntry[] = [];
  const lawFirmFees: LedgerEntry[] = [];
  const barristerFees: LedgerEntry[] = [];
  const recharges: LedgerEntry[] = [];
  const settlements: LedgerEntry[] = [];
  const quantums: LedgerEntry[] = [];
  const recoveriesToPay: LedgerEntry[] = [];
  const shortfalls: LedgerEntry[] = [];
  const enforcement: LedgerEntry[] = [];

  // Two legacy one-shot placements: each cost type's enforcement recovery
  // (value or sentinel) attaches to the FIRST enforcement entry of that
  // type, and a multi-stage re-charge carries the recovered value on its
  // first entry only (later ones still get the applicability sentinel).
  const enforcementRecoveryEmitted = new Set<CostType>();
  let rechargeSeen = false;

  for (const item of payments) {
    const derivation = item.causedBy.derivation;

    // ENFORCEMENT PLAN SLICE — the apportionment derivation is the marker
    // (the slice also cites work origins, so this must be decided before the
    // generic cost-row branch below). One wire entry per (payer, type) /
    // generic slice, amount negated to the party's perspective.
    if (derivation?.rule === "enforcementApportionment") {
      const { costType, enforcedOpponentId } = derivation;
      let recovered: RecoveryAgainstPayment | undefined;
      if (!enforcementRecoveryEmitted.has(costType)) {
        enforcementRecoveryEmitted.add(costType);
        recovered =
          toRecovered(recoveredByEnforcementType.get(costType)) ??
          sentinelIf(recoverableTypes.has(costType));
      }
      enforcement.push({
        kind: "enforcementCost",
        stage: item.stage,
        ...(enforcedOpponentId !== undefined
          ? { payerId: enforcedOpponentId }
          : {}),
        costType,
        amount: -item.amount,
        ...(item.vat !== undefined ? { vat: -item.vat } : {}),
        ...(recovered !== undefined ? { recovered } : {}),
      });
      continue;
    }

    // CFA FEE — there is no stored fee kind: base / success / no-win derive
    // from the derivation (none / successFee / lowFee). Which wire bucket
    // (law-firm fees before barrister fees) follows from the cited agreement
    // origin.
    if (item.mechanism === "fee") {
      // a fee is conditioned by exactly one agreement
      const [agreement] = citedOfKind(item, "agreement");
      const type =
        derivation?.rule === "successFee"
          ? "cfaSuccess"
          : derivation?.rule === "lowFee"
            ? "cfaNoWin"
            : "cfaBase";
      // The recovery of the covered work rides the fee the PARTY pays for
      // it: the base fee, or the no-win fee where that falls due instead,
      // which recovers the work as far as the party pays for it. The success
      // fee never does, and a lawFirm-paid barrister fee reaches the party
      // via the firm's fee or a re-charge instead. The sentinel basis is the
      // covered work's types, widened by the barrister-covered types when
      // the firm's CFA lists barristerFee as an included disbursement (then
      // the barrister work would reach this line even in outcomes where no
      // fees actually passed on).
      let recovered: RecoveryAgainstPayment | undefined;
      if ((type === "cfaBase" || type === "cfaNoWin") && item.payer === "party") {
        const basisTypes = new Set<CostType>(
          citedOfKind(item, "work").map(({ costType }) => costType),
        );
        if (
          agreement?.agreement === "lawFirmCfa" &&
          (agreement.includedDisbursements ?? []).includes("barristerFee")
        ) {
          for (const type of barristerCoveredTypes) basisTypes.add(type);
        }
        recovered =
          recoveredFor(item) ??
          sentinelIf(
            [...basisTypes].some((type) => recoverableTypes.has(type)),
          );
      }
      const entry: LedgerAgreementFeeEntry = {
        kind: "agreementFee",
        // the wire's endpoint unions (party|lawFirm pays, lawFirm|barrister
        // receives) are narrower than EntityRef — validator-pinned by the
        // fee-endpoint rule, asserted here
        payer: item.payer as LedgerAgreementFeeEntry["payer"],
        recipient: item.payee as LedgerAgreementFeeEntry["recipient"],
        type,
        stage: item.stage,
        amount: item.amount,
        ...(item.vat !== undefined ? { vat: item.vat } : {}),
        ...(recovered !== undefined ? { recovered } : {}),
      };
      (agreement?.agreement === "barristerCfa"
        ? barristerFees
        : lawFirmFees
      ).push(entry);
      continue;
    }

    // RE-CHARGE — a billing whose cause is fee PAYMENTS instead of work:
    // the firm passing its barrister-CFA outlay on to the party. That edge
    // shape IS the discriminator; "recharge" exists on the wire only as a
    // display label. The barrister WORK's recovery rides this item (the
    // party pays for that work here); the success element inside the amount
    // is never recovered.
    if (
      item.mechanism === "billing" &&
      (item.causedBy.payments?.length ?? 0) > 0
    ) {
      const recovered = rechargeSeen
        ? sentinelIf(recoverableTypes.has("barristerFee"))
        : (recoveredFor(item) ??
          sentinelIf(recoverableTypes.has("barristerFee")));
      rechargeSeen = true;
      recharges.push({
        kind: "agreementFee",
        payer: "party",
        recipient: "lawFirm",
        type: "recharge",
        stage: item.stage,
        amount: item.amount,
        ...(item.vat !== undefined ? { vat: item.vat } : {}),
        ...(recovered !== undefined ? { recovered } : {}),
      });
      continue;
    }

    // COST ROW — every remaining billing/absorption citing a work origin
    // becomes one cost entry on its PAYER's perspective: the party's own
    // bill, the firm's conventional payment of a covered disbursement, or a
    // provider's absorbed work. The provider income that `signedAmountFor`
    // flips out of these entries at read time is already explicit in the
    // item graph (payee side), so nothing extra is emitted here. Recovery
    // (value or sentinel) attaches only to the party's own bill — provider
    // legs are never recoverable rows.
    if (item.mechanism === "billing" || item.mechanism === "absorption") {
      // these payments are emitted per budget cost, so each cites exactly
      // ONE work item (the validator pins this); the multi-work billings —
      // the plan-apportioned enforcement spend — took the branch above
      const [work] = citedOfKind(item, "work");
      if (work === undefined) continue;
      // cost rows live on fold entities — the emitter never produces other
      // payers here (validator-pinned); lenient skip if data says otherwise
      const entity = entityOf(item.payer);
      if (entity === undefined) continue;
      const recovered =
        item.mechanism === "billing" && item.payer === "party"
          ? (recoveredFor(item) ??
            sentinelIf(recoverableTypes.has(work.costType)))
          : undefined;
      costs.push({
        kind: "cost",
        entity,
        stage: item.stage,
        costId: work.costId,
        costType: work.costType,
        estimated: item.estimated === true,
        amount: -item.amount,
        ...(item.vat !== undefined ? { vat: -item.vat } : {}),
        ...(work.settlementId !== undefined
          ? { settlementId: work.settlementId }
          : {}),
        ...(recovered !== undefined ? { recovered } : {}),
      });
      continue;
    }

    // ADJUSTMENTS — outcome-event transfers, told apart by the origin kinds
    // they cite: settlement → settlement entry; a single quantum award →
    // quantum entry; a single adverse costs order → recoveryToPay; recovery
    // (work + awarded order) → already folded into its host; everything
    // else is the shortfall reconciliation. Signed amounts re-derive from
    // the payment direction (payee party = money in).
    if (item.mechanism === "adjustment") {
      if (isRecovery(item)) continue; // folded into its host above

      const settlement = citedOrigins(item).find(isOriginOfKind("settlement"));
      if (settlement !== undefined) {
        settlements.push({
          kind: "settlement",
          stage: item.stage,
          amount: item.payee === "party" ? item.amount : -item.amount,
          counterpartyName: settlement.counterpartyName,
          breakdown: {
            ofSettlement: settlement.breakdown.ofSettlement,
            towardsPayer: settlement.breakdown.towardsPayer,
            generic: settlement.breakdown.generic,
            quantums: settlement.breakdown.quantums,
          },
        });
        continue;
      }

      // Quantum / adverse-costs payments classify by their cited origin
      // KIND. The shortfall cites the same kinds, but it is a
      // reconciliation, and a reconciliation cites the judgment PAYMENTS it
      // reconciles (its defining property — the validator's judgment rule
      // sums exactly those citations). So: payment-citing adjustments fall
      // through to the shortfall catch-all, payment-less ones are the
      // quantum / adverse-costs payments themselves.
      const hasPayments = (item.causedBy.payments?.length ?? 0) > 0;
      const quantum = citedOrigins(item).find(isOriginOfKind("quantumAward"));
      if (quantum !== undefined && !hasPayments) {
        quantums.push(
          quantum.direction === "awarded"
            ? {
                kind: "quantumAwarded",
                stage: item.stage,
                quantumName: quantum.quantumName,
                amount: item.amount,
              }
            : {
                kind: "quantumToPay",
                stage: item.stage,
                quantumName: quantum.quantumName,
                amount: -item.amount,
              },
        );
        continue;
      }

      const against = citedOfKind(item, "costsOrder").find(
        (origin) => origin.direction === "against",
      );
      if (against?.recipientPartyId !== undefined && !hasPayments) {
        recoveriesToPay.push({
          kind: "recoveryToPay",
          stage: item.stage,
          toRecipients: [partyName(against.recipientPartyId)],
          amount: -item.amount,
        });
        continue;
      }

      // Everything else reconciles the judgment against what the plan
      // collected (or against zero when Enforcement was never reached) —
      // the enforcement shortfall.
      shortfalls.push({
        kind: "enforcementShortfall",
        stage: item.stage,
        amount: item.payee === "party" ? item.amount : -item.amount,
      });
    }
  }

  // The legacy wire order — see the bucket comment above.
  return [
    ...costs,
    ...lawFirmFees,
    ...barristerFees,
    ...recharges,
    ...settlements,
    ...quantums,
    ...recoveriesToPay,
    ...shortfalls,
    ...enforcement,
  ];
}
